import * as v from "valibot";

import Bugsnag from "@/utils/bugsnag";
import { type DisplayEvent, toDisplayEvent } from "@/utils/event-helpers";
import {
  type EventMemberDetail,
  EventMemberDetailSchema,
} from "@/utils/schemas";
import { supabase } from "@/utils/supabase";

const EVENT_SELECT = "*, event_ticket_kind(*)";

export interface EventGuest {
  isMutual: boolean;
  isSelf: boolean;
  name: string;
  role: string;
  user_id: string;
}

export interface EventMembers {
  guests: EventGuest[];
  hosts: string[];
  myStatus: null | string;
}

export async function fetchEventBySlug(
  slug: string,
): Promise<DisplayEvent | null> {
  try {
    const { data, error } = await supabase
      .from("event")
      .select(EVENT_SELECT)
      .eq("slug", slug)
      .single();

    if (error) throw error;
    if (!data) return null;
    return toDisplayEvent(data);
  } catch (e) {
    console.error("fetchEventBySlug error", e);
    Bugsnag.notify(e instanceof Error ? e : new Error(String(e)));
    return null;
  }
}

export async function fetchEventMemberDetails(
  eventId: string,
): Promise<EventMemberDetail[]> {
  try {
    const { data: members, error } = await supabase
      .from("v1_event_member_detail")
      .select("user_id, role, rsvp_status, name, is_contact")
      .eq("event_id", eventId);

    Bugsnag.leaveBreadcrumb("fetchEventMemberDetails", {
      error: String(error),
      memberCount: String(members?.length ?? 0),
    });
    if (error) {
      throw error;
    }

    return v.parse(v.array(EventMemberDetailSchema), members);
  } catch (e) {
    console.error("fetchEventMemberDetails error", e);
    Bugsnag.notify(e instanceof Error ? e : new Error(String(e)));
    return [];
  }
}

export async function fetchSingleEventMember(
  eventId: string,
  userId: string,
): Promise<EventMemberDetail | null> {
  try {
    const { data, error } = await supabase
      .from("v1_event_member_detail")
      .select("user_id, role, rsvp_status, name, is_contact")
      .eq("event_id", eventId)
      .eq("user_id", userId)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;
    return v.parse(EventMemberDetailSchema, data);
  } catch (e) {
    console.error("fetchSingleEventMember error", e);
    Bugsnag.notify(e instanceof Error ? e : new Error(String(e)));
    return null;
  }
}

export async function insertEvent(
  eventData: Record<string, unknown>,
): Promise<{ id: string } | null> {
  try {
    const { data, error } = await supabase
      .from("event")
      .insert(eventData)
      .select("id")
      .single();

    if (error || !data) {
      console.error("insertEvent error", error);
      Bugsnag.notify(new Error(`insertEvent error: ${error?.message}`));
      return null;
    }
    return data;
  } catch (e) {
    console.error("insertEvent error", e);
    Bugsnag.notify(e instanceof Error ? e : new Error(String(e)));
    return null;
  }
}

export async function upsertRsvpStatus(
  eventId: string,
  userId: string,
  rsvpStatus: "cancelled" | "confirmed" | "maybe",
  ticketKindId?: null | string,
): Promise<boolean> {
  try {
    const payload: Record<string, string> = {
      event_id: eventId,
      rsvp_status: rsvpStatus,
      user_id: userId,
    };
    if (ticketKindId) {
      payload.event_ticket_kind_id = ticketKindId;
    }
    console.log("upsertRsvpStatus", payload);
    const { error } = await supabase
      .from("event_member")
      .upsert(payload, { onConflict: "event_id,user_id" });
    if (error) {
      console.error("upsertRsvpStatus error", error);
      Bugsnag.notify(new Error(`upsertRsvpStatus error: ${error.message}`));
      return false;
    }
    return true;
  } catch (e) {
    console.error("upsertRsvpStatus error", e);
    Bugsnag.notify(e instanceof Error ? e : new Error(String(e)));
    return false;
  }
}
